import React from 'react';
import { artistInfo, legalInfo } from '../data/mock';
import { Instagram, Youtube, Music2, Link2 } from 'lucide-react';

// TikTok icon component
const TikTokIcon = ({ size = 20, className = "" }) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    fill="currentColor" 
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="contact" className="bg-black border-t border-neutral-800 py-16">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="/media/logo-gail-lleyton.png" 
                alt="Gail Lleyton Logo" 
                className="h-16 w-auto"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <h3 className="font-display text-3xl font-bold text-white mb-2 tracking-wider">
              GAIL LLEYTON
            </h3>
            <p className="text-gray-400 mb-6 max-w-md">
              {artistInfo.tagline}
            </p>
            <div className="flex gap-4">
              <a
                href={artistInfo.social.spotify}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-gradient-to-br from-rose-900 to-rose-700 hover:from-rose-800 hover:to-rose-600 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-rose-500/50"
                title="Spotify"
              >
                <Music2 size={20} className="text-white" />
              </a>
              <a
                href={artistInfo.social.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-gradient-to-br from-rose-900 to-violet-800 hover:from-rose-800 hover:to-violet-700 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-violet-500/50"
                title="YouTube"
              >
                <Youtube size={20} className="text-white" />
              </a>
              <a
                href={artistInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-gradient-to-br from-violet-900 to-rose-700 hover:from-violet-800 hover:to-rose-600 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-rose-500/50"
                title="Instagram"
              >
                <Instagram size={20} className="text-white" />
              </a>
              <a
                href={artistInfo.social.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-gradient-to-br from-rose-900 to-violet-900 hover:from-rose-800 hover:to-violet-800 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-violet-500/50"
                title="TikTok"
              >
                <TikTokIcon size={20} className="text-white" />
              </a>
              <a
                href={artistInfo.social.linktree}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-gradient-to-br from-violet-900 to-pink-900 hover:from-violet-800 hover:to-pink-800 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-pink-500/50"
                title="Linktree"
              >
                <Link2 size={20} className="text-white" />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white font-semibold mb-4 uppercase tracking-wider text-sm">Navigation</h4>
            <ul className="space-y-2">
              <li>
                <a href="/#about" className="text-gray-400 hover:text-white transition-colors">
                  À Propos
                </a>
              </li>
              <li>
                <a href="/#discography" className="text-gray-400 hover:text-white transition-colors">
                  Discographie
                </a>
              </li>
              <li>
                <a href="/#beats" className="text-gray-400 hover:text-white transition-colors">
                  Beats
                </a>
              </li>
              <li>
                <a href="/#videos" className="text-gray-400 hover:text-white transition-colors">
                  Vidéos
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-4 uppercase tracking-wider text-sm">Services</h4>
            <ul className="space-y-2">
              <li>
                <a href="/#service-beats-production" className="text-gray-400 hover:text-white transition-colors">
                  Beats & Production
                </a>
              </li>
              <li>
                <a href="/#service-conseils" className="text-gray-400 hover:text-white transition-colors">
                  Conseils Personnalisés
                </a>
              </li>
              <li>
                <a href="/#service-mixage-mastering" className="text-gray-400 hover:text-white transition-colors">
                  Mixage & Mastering
                </a>
              </li>
              <li>
                <a href="/#service-visuel-infographie" className="text-gray-400 hover:text-white transition-colors">
                  Visuel & Infographie
                </a>
              </li>
              <li>
                <a href="/#service-montage-video" className="text-gray-400 hover:text-white transition-colors">
                  Montage & Vidéo
                </a>
              </li>
              <li>
                <a href="/#service-site-web-vitrine" className="text-gray-400 hover:text-white transition-colors">
                  Site Web vitrine
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-neutral-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Gail Lleyton. Tous droits réservés.
          </p>
          <div className="flex gap-6 text-sm">
            <a href={legalInfo.mentionsLegales} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white transition-colors">
              Mentions Légales
            </a>
            <a href={legalInfo.politiqueConfidentialite} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white transition-colors">
              Confidentialité
            </a>
            <a 
              href="mailto:contact@GailLleyton.com?subject=%5BSite%20officiel%20%E2%80%93%20Gail%20Lleyton%5D%20Nouveau%20message"
              onClick={(e) => e.stopPropagation()}
              className="text-gray-500 hover:text-white transition-colors"
            >
              Contact
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;